import { useState, useRef, useCallback } from "react"
import {
  Sparkles, Upload, FileText, Trash2, Save, Loader2, Check, Info,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
  useAIInstructions, useUpdateAIInstructions,
  useAIKnowledge, useUploadKnowledge, useDeleteKnowledge,
  usePartnerInstructions, useUpdatePartnerInstructions,
} from "@/hooks/useOutreach"
import { useAuthStore } from "@/stores/authStore"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"

const TONE_OPTIONS = [
  { value: "consultivo", label: "Consultivo" },
  { value: "direto", label: "Direto" },
  { value: "amigavel", label: "Amigavel" },
  { value: "formal", label: "Formal" },
  { value: "tecnico", label: "Tecnico" },
]

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  return (bytes / (1024 * 1024)).toFixed(1) + " MB"
}

export default function AISettingsTab() {
  const user = useAuthStore((s) => s.user)
  const isBDR = user?.role?.name === "sales_rep"

  // Global AI Instructions
  const { data: instructions, isLoading: loadingInstr } = useAIInstructions()
  const updateInstructions = useUpdateAIInstructions()
  const [globalText, setGlobalText] = useState("")
  const [confirmDeleteFileId, setConfirmDeleteFileId] = useState<string | null>(null)
  const [tone, setTone] = useState("consultivo")
  const [socialProof, setSocialProof] = useState(true)
  const [instrLoaded, setInstrLoaded] = useState(false)
  const [instrSaved, setInstrSaved] = useState(false)

  // Sync from server
  if (instructions && !instrLoaded) {
    setGlobalText(instructions.global_instructions || "")
    setTone(instructions.tone || "consultivo")
    setSocialProof(instructions.include_social_proof ?? true)
    setInstrLoaded(true)
  }

  // Knowledge Base
  const { data: knowledgeData, isLoading: loadingKnowledge } = useAIKnowledge()
  const uploadKnowledge = useUploadKnowledge()
  const deleteKnowledge = useDeleteKnowledge()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  // Partner instructions (BDR only)
  const { data: partnerData } = usePartnerInstructions()
  const updatePartner = useUpdatePartnerInstructions()
  const [partnerText, setPartnerText] = useState("")
  const [partnerLoaded, setPartnerLoaded] = useState(false)
  const [partnerSaved, setPartnerSaved] = useState(false)

  if (partnerData && !partnerLoaded) {
    setPartnerText(partnerData.text || "")
    setPartnerLoaded(true)
  }

  const knowledgeItems = knowledgeData?.items || []

  const handleSaveInstructions = async () => {
    try {
      await updateInstructions.mutateAsync({
        global_instructions: globalText,
        tone,
        include_social_proof: socialProof,
      })
      setInstrSaved(true)
      setTimeout(() => setInstrSaved(false), 2000)
    } catch (err) {
      console.error("Save instructions failed:", err)
    }
  }

  const handleUpload = useCallback(async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`Arquivo ${file.name} excede 10MB`)
        continue
      }
      try {
        await uploadKnowledge.mutateAsync(file)
      } catch {
        alert(`Erro ao enviar ${file.name}`)
      }
    }
  }, [uploadKnowledge])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length) handleUpload(e.dataTransfer.files)
  }, [handleUpload])

  const handleDeleteFile = async (id: string) => {
    try {
      await deleteKnowledge.mutateAsync(id)
    } catch {
      alert("Erro ao remover arquivo")
    }
  }

  const handleSavePartner = async () => {
    try {
      await updatePartner.mutateAsync({ text: partnerText })
      setPartnerSaved(true)
      setTimeout(() => setPartnerSaved(false), 2000)
    } catch (err) {
      console.error("Save partner instructions failed:", err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div>
          <h2 className="text-lg font-bold">Inteligencia IA — Outreach</h2>
          <p className="text-sm text-muted-foreground">
            Configuracoes de IA para geracao de mensagens de prospeccao
          </p>
        </div>
      </div>

      {/* Knowledge Base */}
      <div className="border rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Base de Conhecimento
        </h3>
        <p className="text-xs text-muted-foreground">
          Envie documentos sobre a empresa, produtos, cases de sucesso. A IA usa esse contexto para personalizar mensagens.
        </p>

        {/* Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            dragOver
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50 hover:bg-accent/30",
          )}
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium">Arraste arquivos aqui ou clique para selecionar</p>
          <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, TXT (max 10MB)</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.txt,.md"
            className="hidden"
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
        </div>

        {uploadKnowledge.isPending && (
          <div className="flex items-center gap-2 text-sm text-blue-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando arquivo...
          </div>
        )}

        {/* Uploaded Files List */}
        {loadingKnowledge ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando...
          </div>
        ) : knowledgeItems.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Arquivos carregados ({knowledgeItems.length})
            </p>
            {knowledgeItems.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-muted/30">
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item.filename}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {item.category} {item.size_bytes ? ` — ${formatBytes(item.size_bytes)}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => setConfirmDeleteFileId(item.id)}
                  disabled={deleteKnowledge.isPending}
                  className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">Nenhum arquivo na base de conhecimento.</p>
        )}
      </div>

      {/* AI Instructions */}
      <div className="border rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          Instrucoes para a IA
        </h3>
        <p className="text-xs text-muted-foreground">
          Defina como a IA deve se comportar ao gerar mensagens. Essas instrucoes sao aplicadas a todos os usuarios.
        </p>
        <textarea
          value={globalText}
          onChange={(e) => setGlobalText(e.target.value)}
          rows={10}
          className="w-full rounded-lg border bg-background px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
          placeholder="Voce e um especialista comercial da NAI S/A, empresa de governanca regulatoria integrada. Ao gerar mensagens de prospeccao, considere..."
        />
      </div>

      {/* Parameters */}
      <div className="border rounded-lg p-5 space-y-4">
        <h3 className="text-sm font-semibold">Parametros</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Tom de voz</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="mt-1 w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {TONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Social proof</label>
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={() => setSocialProof(!socialProof)}
                className={cn(
                  "relative w-11 h-6 rounded-full transition-colors",
                  socialProof ? "bg-primary" : "bg-zinc-300 dark:bg-zinc-600",
                )}
              >
                <div
                  className={cn(
                    "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
                    socialProof ? "translate-x-[22px]" : "translate-x-0.5",
                  )}
                />
              </button>
              <span className="text-sm">{socialProof ? "Incluir cases e provas sociais" : "Desativado"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveInstructions}
          disabled={updateInstructions.isPending || loadingInstr}
          className={cn(
            "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all",
            instrSaved
              ? "bg-green-600"
              : updateInstructions.isPending
                ? "bg-primary/60 cursor-not-allowed"
                : "bg-primary hover:bg-primary/90",
          )}
        >
          {instrSaved ? (
            <>
              <Check className="w-4 h-4" /> Salvo!
            </>
          ) : updateInstructions.isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" /> Salvar Configuracoes
            </>
          )}
        </button>
      </div>

      {/* Partner / BDR Section */}
      {isBDR && (
        <div className="border-t pt-6 mt-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Configuracoes do Parceiro BDR</h3>
              <p className="text-xs text-muted-foreground">
                Instrucoes adicionais que personalizam as mensagens geradas pela IA para sua consultoria.
              </p>
            </div>
          </div>

          <textarea
            value={partnerText}
            onChange={(e) => setPartnerText(e.target.value)}
            rows={8}
            className="w-full rounded-lg border bg-background px-3 py-2.5 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary font-mono"
            placeholder="Me apresente como Consultor da [Minha Empresa], parceiro autorizado da NAI S/A. Ao gerar mensagens, use o nome da minha empresa como remetente..."
          />

          <div className="flex justify-end">
            <button
              onClick={handleSavePartner}
              disabled={updatePartner.isPending}
              className={cn(
                "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-all",
                partnerSaved
                  ? "bg-green-600"
                  : updatePartner.isPending
                    ? "bg-primary/60 cursor-not-allowed"
                    : "bg-primary hover:bg-primary/90",
              )}
            >
              {partnerSaved ? (
                <>
                  <Check className="w-4 h-4" /> Salvo!
                </>
              ) : updatePartner.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Salvar Instrucoes do Parceiro
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteFileId}
        title="Remover arquivo da base?"
        message="O arquivo será removido da base de conhecimento. Esta ação não pode ser desfeita."
        confirmLabel="Remover"
        variant="destructive"
        isLoading={deleteKnowledge.isPending}
        onConfirm={async () => {
          if (!confirmDeleteFileId) return
          await handleDeleteFile(confirmDeleteFileId)
          setConfirmDeleteFileId(null)
        }}
        onCancel={() => setConfirmDeleteFileId(null)}
      />
    </div>
  )
}
